import { Component, Injector } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudRevisionEntity, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { Asset, ASSET_TYPE, AssetRevision } from 'assets-model';
import { AssetService, AssetTypeService, BusinessUnitService, CostCentreService, MechanicalAspectService, RefCodeStorage } from 'assets-service';
import { ASSET_SEARCH_ALL_ASPECT_COLUMNS, ASSET_SEARCH_ELECTRICAL_ASPECT_COLUMNS, ASSET_SEARCH_MAINTENANCE_ASPECT_COLUMNS, ASSET_SEARCH_MECHANICAL_ASPECT_COLUMNS, ASSET_SEARCH_RESULT_DATA_COLUMNS, AssetSearchComponent } from '../search/asset-search.component';

export const ASSET_AUDIT_LIST_COLUMNS = ['revisionDate', 'revisionUsername', ...ASSET_SEARCH_RESULT_DATA_COLUMNS, 'tags', ...ASSET_SEARCH_ALL_ASPECT_COLUMNS];

@Component({
  selector: 'app-asset-audit-list',
  templateUrl: './asset-audit-list.component.html',
  styleUrls: ['./asset-audit-list.component.scss']
})
export class AssetAuditListComponent extends AssetSearchComponent {

  auditDataSource: AssetRevision[] = [];
  _item: Asset;

  displayedColumns: string[] = [...ASSET_AUDIT_LIST_COLUMNS];
  disabledColumns: string[] = ['assetId'];

  get auditAssetId() {
    return this.routeParams?.assetId;
  }

  get item() {
    if (this._item != null) {
      return this._item;
    } else if (this.auditDataSource != null) {
      return this.auditDataSource[0];
    } else return null;
  }

  get hasHadMechanicalAspect() {
    for (let revision of this.auditDataSource) {
      if (revision.hasMechanicalAspect) {
        return true;
      }
    }
    return false;
  }

  get hasHadElectricalAspect() {
    for (let revision of this.auditDataSource) {
      if (revision.hasElectricalAspect) {
        return true;
      }
    }
    return false;
  }

  get hasHadMaintenanceAspect() {
    for (let revision of this.auditDataSource) {
      if (revision.hasMaintenanceAspect) {
        return true;
      }
    }
    return false;
  }

  assetPreviousRevisionGetter = previousRevisionGetterBuilder(() => this.auditDataSource);

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected assetService: AssetService,
    protected assetTypeService: AssetTypeService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected mechanicalAspectService: MechanicalAspectService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(route, router, userStorage, dialog, assetService, assetTypeService, businessUnitService, costCentreService, mechanicalAspectService, refCodeStorage, fb, formStorage, injector);
    this.autoSearch = false;
  }

  protected onInitAsyncComplete(): void {
    super.onInitAsyncComplete();
    this.assetService.get(this.auditAssetId).subscribe(
      data => {
        this._item = data
      }
    )
  }

  disableUnusedAspectColumns() {
    this.columns.setColumnsEnabled(ASSET_SEARCH_MECHANICAL_ASPECT_COLUMNS, this.hasHadMechanicalAspect);
    this.columns.setColumnsEnabled(ASSET_SEARCH_ELECTRICAL_ASPECT_COLUMNS, this.hasHadElectricalAspect);
    this.columns.setColumnsEnabled(ASSET_SEARCH_MAINTENANCE_ASPECT_COLUMNS, this.hasHadMaintenanceAspect);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.sort.disabled = true;
  }

  backRoute(): any[] {
    return [...ASSET_TYPE.editRouteBase, this.auditAssetId];
  }

  loadPage() {
    this.assetService.findAuditHistory(this.auditAssetId).subscribe(
      data => {
        this.auditDataSource = (<AssetRevision[]>data)
        this.disableUnusedAspectColumns();
      }
    )
  }

  //disable actual search functionality
  createServiceArguments() {
    return {}
  }

  cellClass(element: AssetRevision, fieldName: string, relationshipGetter?: (e: AssetRevision) => any, previousRevisionGetter?: (e: AssetRevision) => AssetRevision): any {
    return auditCellClass(relationshipGetter, previousRevisionGetter)(element, fieldName);
  }


}

export function previousRevisionGetterBuilder(revisionListGetter: () => CrudRevisionEntity[]): (currentRevision: CrudRevisionEntity) => CrudRevisionEntity {
  return (currentRevision: CrudRevisionEntity) => {
    var previousRevision = null;
    if (revisionListGetter != null && revisionListGetter() != null) {
      for (let rev of revisionListGetter().sort((a, b) => new Date(a._revisionInfo.revisionDate).getTime() - new Date(b._revisionInfo.revisionDate).getTime())) {
        if (currentRevision._revisionInfo.id === rev._revisionInfo.id) {
          break;
        } else {
          previousRevision = rev;
        }
      }
    }
    return previousRevision;
  }
}

export function auditCellClass(relationshipGetter: (e: CrudRevisionEntity) => any, previousRevisionGetter: (e: CrudRevisionEntity) => CrudRevisionEntity): any {
  return (element: CrudRevisionEntity, fieldName: string) => {
    //check if this is called with a relationshipGetter
    if (relationshipGetter != null) {
      //if the relationship is null, then show as 'null-relationship'
      if (relationshipGetter(element) == null && !element?.fieldModified(fieldName)) {
        return { "null-relationship": true }
        //otherwise if the relationship's field is modified...
      } else if (element?.fieldModified(fieldName)) {
        var relatedEntity = relationshipGetter(element)
        //if the field is shown as modified but is null...
        if (element._revisionType == 'MOD' && (relatedEntity == null || relatedEntity[fieldName] === null || relatedEntity[fieldName] === '')) {
          //check if there was a previous revision with the relationship, if so we can mark this as nulled
          var previousRevision = previousRevisionGetter(element);
          var previousRelatedEntity = previousRevision != null ? relationshipGetter(previousRevision) : null;
          if (previousRelatedEntity != null) {
            return { "modified-null": true }
            //otherwise this is just a related entity that has been added with a field that happens to be null, so show as modified
          } else {
            return { "modified": true }
          }
        } else {
          return { "modified": true }
        }
      }
    }
    //otherwise just check if the field is modified
    if (element?.fieldModified(fieldName) || element._revisionType == 'ADD') {
      if (element._revisionType == 'MOD' && (element === null || element[fieldName] === null || element[fieldName] === '')) {
        return { "modified-null": true }
      } else {
        return { "modified": true }
      }
    }
  }
}
